import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Visibility,
  Edit,
  Download,
  Description,
  Person,
  School,
  CheckCircle,
  Pending,
  Schedule,
  Close,
  Refresh,
  FilterList,
  PictureAsPdf,
  Image as ImageIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';

// Add comprehensive CSS to prevent all placeholder requests
const style = document.createElement('style');
style.textContent = `
  img[src*="placeholder"] {
    display: none !important;
  }
  img[src*="60/60"] {
    display: none !important;
  }
  /* Hide any image that tries to load placeholder URLs */
  img[src*="api/placeholder"] {
    display: none !important;
  }
  /* Block any image with placeholder in the URL */
  img[src*="placeholder" i] {
    display: none !important;
  }
  /* Block MUI Avatar fallback images */
  img[alt][src*="60"] {
    display: none !important;
  }
  /* Block any image that might be a placeholder */
  img:not([src*="uploads"]):not([src*="http"]):not([src*="data"]) {
    display: none !important;
  }
`;
document.head.appendChild(style);

const SimpleApplicationManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    under_review: 0,
    approved: 0,
    rejected: 0
  });
  
  // Applications
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [reviewTabValue, setReviewTabValue] = useState(0);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Status update form
  const [statusForm, setStatusForm] = useState({
    application_status: 'pending',
    review_notes: ''
  });

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/enhanced-admissions/stats');
      if (response.data.success) {
        const overview = response.data.data.overview;
        setStats({
          total: overview.total_applications || 0,
          pending: overview.pending_review || 0,
          under_review: overview.pending_review || 0,
          approved: overview.admitted || 0,
          rejected: overview.rejected || 0
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch applications
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 50,
        search: searchTerm
      };
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      if (classFilter !== 'all') {
        params.class_filter = classFilter;
      }
      
      const response = await axios.get('/api/enhanced-admissions/applications', { params });
      
      if (response.data.success) {
        setApplications(response.data.data || []);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch applications', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch application details
  const fetchApplicationDetails = async (applicationId) => {
    try {
      const response = await axios.get(`/api/enhanced-admissions/application/${applicationId}`);
      if (response.data.success) {
        setSelectedApplication(response.data.data);
        setStatusForm({
          application_status: response.data.data.application.application_status || 'pending',
          review_notes: response.data.data.application.review_notes || ''
        });
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch application details', { variant: 'error' });
    }
  };

  // Update application status
  const updateStatus = async () => {
    try {
      const payload = {
        application_status: statusForm.application_status,
        review_notes: statusForm.review_notes
      };
      
      const response = await axios.put(
        `/api/enhanced-admissions/application/${selectedApplication.application.application_id}/status`,
        payload
      );
      
      if (response.data.success) {
        enqueueSnackbar('Status updated successfully', { variant: 'success' });
        setStatusDialogOpen(false);
        fetchApplications();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'admitted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'under_review':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'admitted':
        return <CheckCircle color="success" />;
      case 'rejected':
        return <Close color="error" />;
      case 'under_review':
        return <Schedule color="warning" />;
      default:
        return <Pending color="default" />;
    }
  };

  // Download document with fetch to prevent corruption - fix PDF MIME type
  const downloadDocument = async (filePath, fileName) => {
    try {
      console.log('Attempting to download:', filePath, fileName);
      const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
      // Ensure the file path includes uploads/admissions/
      const fullPath = filePath.startsWith('uploads/') ? filePath : `uploads/admissions/${filePath}`;
      const fullUrl = `${baseUrl}/${fullPath}`;
      
      console.log('Full download URL:', fullUrl);
      
      // Use fetch to get the file as blob with proper headers
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': '*/*',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the blob with correct MIME type
      const blob = await response.blob();
      console.log('Blob type:', blob.type, 'Size:', blob.size);
      
      // Create object URL with proper MIME type for PDFs
      let finalBlob = blob;
      if (fileName.toLowerCase().endsWith('.pdf') && blob.type !== 'application/pdf') {
        finalBlob = new Blob([blob], { type: 'application/pdf' });
        console.log('Corrected PDF blob type:', finalBlob.type);
      }
      
      const url = window.URL.createObjectURL(finalBlob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      enqueueSnackbar('Download completed', { variant: 'success' });
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar('Failed to download document', { variant: 'error' });
    }
  };

  // View document in dialog with proper error handling
  const viewDocument = (document) => {
    console.log('Viewing document:', document);
    setSelectedDocument(document);
    setDocumentViewerOpen(true);
    
    // Reset image states
    setImageDataUrl(null);
    setImageLoading(false);
    setImageError(null);
    
    // Load image if it's an image type
    if (document.mime_type?.includes('image')) {
      setImageLoading(true);
      const imageUrl = getFileUrl(document.file_path);
      console.log('🔄 Starting image load for:', imageUrl);
      console.log('🎯 Since URL works in new tab, forcing data URL approach...');
      
      // Skip direct test and go straight to fetch (we know URL works)
      fetch(imageUrl)
        .then(response => {
          console.log('📡 Fetch response:', response.status, response.statusText);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          console.log('📦 Blob created:', blob.type, blob.size);
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        })
        .then(dataUrl => {
          console.log('🖼️ Data URL created, length:', dataUrl.length);
          setImageDataUrl(dataUrl);
          setImageLoading(false);
          setImageError(null);
          
          // Test the data URL immediately
          const testImg = new Image();
          testImg.onload = () => {
            console.log('✅ Data URL test successful!');
          };
          testImg.onerror = () => {
            console.error('❌ Data URL test failed!');
            setImageError('Data URL created but failed to display');
          };
          testImg.src = dataUrl;
        })
        .catch(error => {
          console.error('❌ Fetch failed:', error);
          setImageError(`Failed to load image: ${error.message}`);
          setImageLoading(false);
        });
    }
  };

  // Close document viewer
  const closeDocumentViewer = () => {
    setDocumentViewerOpen(false);
    setSelectedDocument(null);
    setImageDataUrl(null);
    setImageLoading(false);
    setImageError(null);
  };

  // Get direct file URL for iframe - fix missing uploads/admissions path
  const getFileUrl = (filePath) => {
    const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    // Ensure the file path includes uploads/admissions/
    const fullPath = filePath.startsWith('uploads/') ? filePath : `uploads/admissions/${filePath}`;
    // Add timestamp to bypass CORS cache issues
    const timestamp = Date.now();
    return `${baseUrl}/${fullPath}?t=${timestamp}`;
  };

  // Get file icon based on mime type
  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('image')) return <ImageIcon color="primary" />;
    if (mimeType?.includes('pdf')) return <PictureAsPdf color="error" />;
    return <Description color="primary" />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    const kb = bytes / 1024;
    return `${kb.toFixed(1)} KB`;
  };

  useEffect(() => {
    fetchStats();
    fetchApplications();
  }, [statusFilter, classFilter, searchTerm]);

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Application Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and manage student applications
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'primary.main',
                    width: 48,
                    height: 48
                  }}
                >
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Applicants
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'warning.main',
                    width: 48,
                    height: 48
                  }}
                >
                  <Pending />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Review
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'info.main',
                    width: 48,
                    height: 48
                  }}
                >
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.under_review}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Under Review
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'success.main',
                    width: 48,
                    height: 48
                  }}
                >
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.approved}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Approved
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'error.main',
                    width: 48,
                    height: 48
                  }}
                >
                  <Close />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.rejected}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rejected
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card mb={3}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder="Search by name, email, or tracking ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ minWidth: 300, flexGrow: 1 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Class Filter</InputLabel>
              <Select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                label="Class Filter"
              >
                <MenuItem value="all">All Classes</MenuItem>
                <MenuItem value="1">Class 1</MenuItem>
                <MenuItem value="2">Class 2</MenuItem>
                <MenuItem value="3">Class 3</MenuItem>
                <MenuItem value="4">Class 4</MenuItem>
                <MenuItem value="5">Class 5</MenuItem>
                <MenuItem value="6">Class 6</MenuItem>
                <MenuItem value="7">Class 7</MenuItem>
                <MenuItem value="8">Class 8</MenuItem>
                <MenuItem value="9">Class 9</MenuItem>
                <MenuItem value="10">Class 10</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                fetchApplications();
                fetchStats();
              }}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        <CardContent>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Applied Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.application_id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main',
                            width: 40,
                            height: 40
                          }}
                        >
                          {app.first_name?.[0]}{app.last_name?.[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {app.first_name} {app.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {app.tracking_id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">{app.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {app.phone}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={`Class ${app.applied_class}`} 
                        variant="outlined" 
                        size="small" 
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getStatusIcon(app.application_status)}
                        <Chip
                          label={app.application_status?.replace('_', ' ') || 'Pending'}
                          color={getStatusColor(app.application_status)}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(app.application_date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              fetchApplicationDetails(app.application_id);
                              setViewDialogOpen(true);
                            }}
                            color="primary"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Update Status">
                          <IconButton
                            size="small"
                            onClick={() => {
                              fetchApplicationDetails(app.application_id);
                              setStatusDialogOpen(true);
                            }}
                            color="secondary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {applications.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                No applications found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* View Application Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Application Details</DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box>
              {/* Personal Information */}
              <Typography variant="h6" gutterBottom>Personal Information</Typography>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">
                    {selectedApplication.application.first_name} {selectedApplication.application.last_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedApplication.application.email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedApplication.application.phone}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Applied Class</Typography>
                  <Typography variant="body1">{selectedApplication.application.applied_class}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Documents */}
              <Typography variant="h6" gutterBottom>Documents</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="center">View</TableCell>
                      <TableCell align="center">Download</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedApplication.documents?.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            {getFileIcon(doc.mime_type)}
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {doc.file_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(doc.file_size)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {doc.document_type?.replace('_', ' ') || 'Document'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              console.log('View button clicked in main dialog:', doc);
                              viewDocument(doc);
                            }}
                            size="small"
                          >
                            <Visibility />
                          </IconButton>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="success"
                            onClick={() => {
                              console.log('Download button clicked in main dialog:', doc.file_path, doc.file_name);
                              downloadDocument(doc.file_path, doc.file_name);
                            }}
                            size="small"
                          >
                            <Download />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {(!selectedApplication.documents || selectedApplication.documents.length === 0) && (
                <Alert severity="info">
                  No documents uploaded with this application
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog 
        open={statusDialogOpen} 
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Review Application & Update Status</DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={reviewTabValue} 
                  onChange={(e, newValue) => setReviewTabValue(newValue)}
                >
                  <Tab label="Application Info" />
                  <Tab label="Documents" />
                  <Tab label="Status Update" />
                </Tabs>
              </Box>
              
              {/* Application Info Tab */}
              {reviewTabValue === 0 && (
                <Box sx={{ p: 3 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Reviewing: {selectedApplication.application.first_name} {selectedApplication.application.last_name}
                  </Alert>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{selectedApplication.application.email}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">{selectedApplication.application.phone}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Applied Class</Typography>
                      <Typography variant="body1">{selectedApplication.application.applied_class}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Current Status</Typography>
                      <Chip
                        label={selectedApplication.application.application_status?.replace('_', ' ') || 'Pending'}
                        color={getStatusColor(selectedApplication.application.application_status)}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Documents Tab */}
              {reviewTabValue === 1 && (
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Review Documents</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>File</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="center">View</TableCell>
                          <TableCell align="center">Download</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedApplication.documents?.map((doc) => (
                          <TableRow key={doc.id}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={2}>
                                {getFileIcon(doc.mime_type)}
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {doc.file_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatFileSize(doc.file_size)}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {doc.document_type?.replace('_', ' ') || 'Document'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                color="primary"
                                onClick={() => {
                                  console.log('View button clicked in review dialog:', doc);
                                  viewDocument(doc);
                                }}
                                size="small"
                              >
                                <Visibility />
                              </IconButton>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                color="success"
                                onClick={() => {
                                  console.log('Download button clicked in review dialog:', doc.file_path, doc.file_name);
                                  downloadDocument(doc.file_path, doc.file_name);
                                }}
                                size="small"
                              >
                                <Download />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {(!selectedApplication.documents || selectedApplication.documents.length === 0) && (
                    <Alert severity="warning">
                      No documents uploaded with this application
                    </Alert>
                  )}
                </Box>
              )}
              
              {/* Status Update Tab */}
              {reviewTabValue === 2 && (
                <Box sx={{ p: 3 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Application Status</InputLabel>
                    <Select
                      value={statusForm.application_status}
                      onChange={(e) => setStatusForm({...statusForm, application_status: e.target.value})}
                      label="Application Status"
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="under_review">Under Review</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Review Notes"
                    multiline
                    rows={4}
                    value={statusForm.review_notes}
                    onChange={(e) => setStatusForm({...statusForm, review_notes: e.target.value})}
                    placeholder="Add notes about this status update..."
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={updateStatus} variant="contained" color="primary">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog 
        open={documentViewerOpen} 
        onClose={closeDocumentViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            minHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Document Viewer: {selectedDocument?.file_name}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => {
                  console.log('Download button clicked in viewer header:', selectedDocument?.file_path, selectedDocument?.file_name);
                  downloadDocument(selectedDocument?.file_path, selectedDocument?.file_name);
                }}
              >
                Download
              </Button>
              <Button onClick={closeDocumentViewer}>Close</Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          height: 'calc(100% - 80px)', 
          minHeight: '500px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {selectedDocument && (
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* Document Info */}
              <Box sx={{ 
                p: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                flexShrink: 0
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">File Name</Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ wordBreak: 'break-all' }}>
                      {selectedDocument.file_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">File Size</Typography>
                    <Typography variant="body1">{formatFileSize(selectedDocument.file_size)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Type</Typography>
                    <Typography variant="body1">{selectedDocument.mime_type}</Typography>
                  </Grid>
                </Grid>
              </Box>
              
              {/* Document Content */}
              <Box sx={{ 
                flexGrow: 1, 
                p: 2, 
                overflow: 'auto', 
                minHeight: 400,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#fafafa',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                m: 2,
                mb: 2
              }}>
                {selectedDocument.mime_type?.includes('image') && (
                  <Box 
                    display="flex" 
                    justifyContent="center" 
                    alignItems="center" 
                    height="100%"
                    minHeight="400"
                    position="relative"
                    sx={{
                      backgroundColor: '#fff',
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      p: 2,
                      zIndex: 1
                    }}
                  >
                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Loading image: {getFileUrl(selectedDocument.file_path)}
                      </Typography>
                      
                      {/* Image container with proper z-index */}
                      <Box 
                        sx={{
                          minHeight: '400px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid #1976d2',
                          borderRadius: '8px',
                          backgroundColor: '#f5f5f5',
                          position: 'relative',
                          zIndex: 10
                        }}
                      >
                        {imageLoading && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{
                              width: 40,
                              height: 40,
                              border: '4px solid #f3f3f3',
                              borderTop: '4px solid #1976d2',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              margin: '0 auto'
                            }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                              Loading image from data URL...
                            </Typography>
                          </Box>
                        )}
                        
                        {imageError && (
                          <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                              ❌ Image Failed to Load
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Error: {imageError}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                              URL: {getFileUrl(selectedDocument.file_path)}
                            </Typography>
                            <Button
                              variant="contained"
                              onClick={() => window.open(getFileUrl(selectedDocument.file_path), '_blank')}
                              sx={{ mr: 1 }}
                            >
                              Open in New Tab
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => viewDocument(selectedDocument)}
                            >
                              Retry
                            </Button>
                          </Box>
                        )}
                        
                        {imageDataUrl && !imageLoading && !imageError && (
                          <Box sx={{ position: 'relative', zIndex: 20, width: '100%' }}>
                            {/* Working image display */}
                            <Box sx={{ 
                              border: '2px solid #4caf50', 
                              borderRadius: '8px', 
                              p: 2, 
                              mb: 2, 
                              backgroundColor: '#e8f5e8',
                              textAlign: 'center'
                            }}>
                              <Typography variant="body2" color="success.dark" sx={{ mb: 2, fontWeight: 'medium' }}>
                                {selectedDocument.file_name} ({formatFileSize(selectedDocument.file_size)})
                              </Typography>
                              <div dangerouslySetInnerHTML={{
                                __html: `
                                  <img 
                                    src="${imageDataUrl}" 
                                    style="
                                      max-width: 100%; 
                                      max-height: 400px; 
                                      display: block; 
                                      border: 1px solid #4caf50; 
                                      background: white;
                                      border-radius: 6px;
                                      box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
                                    " 
                                  />
                                `
                              }} />
                            </Box>
                            
                            {/* Single action button */}
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Button
                                variant="contained"
                                startIcon={<Visibility />}
                                onClick={() => window.open(getFileUrl(selectedDocument.file_path), '_blank')}
                              >
                                View in New Tab
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                
                {selectedDocument.mime_type?.includes('pdf') && (
                  <Box sx={{ height: '100%', minHeight: 400, border: '1px solid #ddd', borderRadius: 1 }}>
                    <object
                      data={getFileUrl(selectedDocument.file_path)}
                      type="application/pdf"
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        minHeight: 400
                      }}
                      onLoad={() => console.log('✅ PDF loaded successfully:', getFileUrl(selectedDocument.file_path))}
                      onError={(e) => {
                        console.error('❌ PDF load error:', e);
                        console.error('❌ Failed URL:', getFileUrl(selectedDocument.file_path));
                        
                        e.target.style.display = 'none';
                        const errorDiv = document.createElement('div');
                        errorDiv.innerHTML = `
                          <div style="text-align: center; padding: 20px;">
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="#d32f2f">
                              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                            </svg>
                            <p style="color: #666; margin: 16px 0;">Failed to load PDF</p>
                            <button onclick="window.open('${getFileUrl(selectedDocument.file_path)}', '_blank')" 
                                    style="padding: 8px 16px; background: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer;">
                              Open in New Tab
                            </button>
                          </div>
                        `;
                        e.target.parentNode.appendChild(errorDiv);
                      }}
                    >
                      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                        <PictureAsPdf sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          PDF Viewer Not Available
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                          Your browser cannot display this PDF inline. Please download or open in a new tab.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Download />}
                          onClick={() => downloadDocument(selectedDocument.file_path, selectedDocument.file_name)}
                          sx={{ mb: 2 }}
                        >
                          Download PDF
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => window.open(getFileUrl(selectedDocument.file_path), '_blank')}
                        >
                          Open in New Tab
                        </Button>
                      </Box>
                    </object>
                  </Box>
                )}
                
                {!selectedDocument.mime_type?.includes('image') && !selectedDocument.mime_type?.includes('pdf') && (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" minHeight={400}>
                    <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Document Preview Not Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                      This file type cannot be previewed. Please download the file to view its contents.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={() => downloadDocument(selectedDocument.file_path, selectedDocument.file_name)}
                      sx={{ mb: 2 }}
                    >
                      Download Document
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => window.open(getFileUrl(selectedDocument.file_path), '_blank')}
                    >
                      Open in New Tab
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SimpleApplicationManagement;
